import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

// Minimum distance between the bubble and the viewport edge in px
const VIEWPORT_GAP = 16;
// Distance between the icon and the bubble in px (see .info-bubble__bubble--*)
const BUBBLE_OFFSET = 8;
// The bubble is never shrunk below this height in px
const MIN_BUBBLE_HEIGHT = 150;

const DEFAULT_POSITION = { placement: 'bottom', shift: 0, maxHeight: null };

/**
 * Info icon that opens a bubble with an explanatory text on click/tap.
 *
 * Renders nothing if no text is given, so the icon is hidden when the text
 * is not maintained in the plugin settings.
 *
 * @param {string} label Accessible name of the button and title of the bubble, e.g. "Anteilscheinkapital"
 * @param {string} text  Sanitized HTML from the plugin settings (options.infoTexts)
 */
const InfoBubble = ({ label, text }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [position, setPosition] = useState(DEFAULT_POSITION);
	const wrapperRef = useRef(null);
	const buttonRef = useRef(null);
	const bubbleRef = useRef(null);
	const contentRef = useRef(null);
	const id = useId();
	const bubbleId = `info-bubble-${id}`;
	const titleId = `info-bubble-title-${id}`;

	const close = (restoreFocus = false) => {
		setIsOpen(false);
		setPosition(DEFAULT_POSITION);
		if (restoreFocus && buttonRef.current) {
			buttonRef.current.focus();
		}
	};

	// Close on click/tap outside and on Escape
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handlePointerDown = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				close();
			}
		};

		const handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				close(true);
			}
		};

		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen]);

	// Keep the bubble inside the viewport: flip above the icon if there is
	// not enough space below, limit its height if it fits on neither side,
	// and shift it horizontally if it overflows.
	useLayoutEffect(() => {
		if (!isOpen || !bubbleRef.current || !buttonRef.current) {
			return;
		}

		// Measured in the default position (below, centered, no shift),
		// which is reset on every close.
		const buttonRect = buttonRef.current.getBoundingClientRect();
		const bubbleRect = bubbleRef.current.getBoundingClientRect();
		const viewportWidth = document.documentElement.clientWidth;

		// The theme sets scroll-padding-top to the height of the fixed header
		const headerOffset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;

		const spaceBelow = window.innerHeight - buttonRect.bottom - BUBBLE_OFFSET - VIEWPORT_GAP;
		const spaceAbove = buttonRect.top - headerOffset - BUBBLE_OFFSET - VIEWPORT_GAP;

		let placement = 'bottom';
		let maxHeight = null;
		if (bubbleRect.height > spaceBelow) {
			if (bubbleRect.height <= spaceAbove) {
				placement = 'top';
			} else {
				// Fits on neither side: use the larger one and scroll the text
				placement = spaceAbove > spaceBelow ? 'top' : 'bottom';
				maxHeight = Math.max(Math.max(spaceAbove, spaceBelow), MIN_BUBBLE_HEIGHT);
			}
		}

		let shift = 0;
		if (bubbleRect.left < VIEWPORT_GAP) {
			shift = VIEWPORT_GAP - bubbleRect.left;
		} else if (bubbleRect.right > viewportWidth - VIEWPORT_GAP) {
			shift = (viewportWidth - VIEWPORT_GAP) - bubbleRect.right;
		}

		setPosition({ placement, shift, maxHeight });
	}, [isOpen]);

	// Move focus into the bubble so screen readers announce its content.
	// Focus goes to the scrollable content, so long texts can be scrolled
	// with the arrow keys.
	useEffect(() => {
		if (isOpen && contentRef.current) {
			contentRef.current.focus({ preventScroll: true });
		}
	}, [isOpen]);

	if (!text) {
		return null;
	}

	return (
		<span className="info-bubble" ref={wrapperRef}>
			&nbsp;
			<button
				type="button"
				className="info-bubble__toggle"
				ref={buttonRef}
				aria-label={`Info zu ${label}`}
				aria-expanded={isOpen}
				aria-controls={bubbleId}
				onClick={() => (isOpen ? close() : setIsOpen(true))}
			>
				<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
					<circle cx="8.5" cy="8.5" r="8.5" fill="#C4C4C4"></circle>
					<path d="M8.96394 4.576V3.12H8.03594V4.576H8.96394ZM8.96394 14V6.368H8.03594V14H8.96394Z" fill="white"></path>
				</svg>
			</button>
			{isOpen && (
				<span
					id={bubbleId}
					className={`info-bubble__bubble info-bubble__bubble--${position.placement}`}
					style={{
						'--info-bubble-shift': `${position.shift}px`,
						maxHeight: position.maxHeight ? `${position.maxHeight}px` : undefined,
					}}
					ref={bubbleRef}
					role="dialog"
					aria-labelledby={titleId}
				>
					<span className="info-bubble__header">
						<span className="info-bubble__title" id={titleId}>{label}</span>
						<button
							type="button"
							className="info-bubble__close"
							aria-label="Schliessen"
							onClick={() => close(true)}
						>
							<span aria-hidden="true">&times;</span>
						</button>
					</span>
					<span
						className="info-bubble__content"
						ref={contentRef}
						tabIndex={-1}
						dangerouslySetInnerHTML={{ __html: text }}
					/>
				</span>
			)}
		</span>
	);
};

export default InfoBubble;
